import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  Group,
  Recurrence,
  Schedule,
  SchedulePreview,
  ScheduleTiming,
} from '@up2d8/contracts';
import { Button, Callout, Dialog, Input, Select, useToast } from '@up2d8/ui';
import { formatDateTime } from '../../shared/format';
import { useSession } from '../../shared/session';

const DAY = 24 * 60 * 60 * 1000;
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

function today(offsetDays = 0): string {
  const date = new Date(Date.now() + offsetDays * DAY);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

function clockIn(minutes: number): string {
  const date = new Date(Date.now() + minutes * 60 * 1000);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** "Elke maandag en donderdag om 07:00", in de woorden van de lijst. */
export function describeSchedule(
  schedule: Pick<
    Schedule,
    | 'recurrence'
    | 'daysOfWeek'
    | 'dayOfMonth'
    | 'monthOfYear'
    | 'timeOfDay'
    | 'deliveryMode'
    | 'shiftGroupName'
    | 'startsOn'
  >,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const days = schedule.daysOfWeek.map((day) => t(`scheduling.day${day}`)).join(', ');
  const wanneer =
    schedule.recurrence === 'weekly'
      ? t('scheduling.summaryWeekly', { days })
      : schedule.recurrence === 'monthly'
        ? t('scheduling.summaryMonthly', {
            day: schedule.dayOfMonth ?? Number(schedule.startsOn.slice(8, 10)),
          })
        : schedule.recurrence === 'yearly'
          ? t('scheduling.summaryYearly', {
              day: schedule.dayOfMonth ?? Number(schedule.startsOn.slice(8, 10)),
              month: t(
                `scheduling.month${schedule.monthOfYear ?? Number(schedule.startsOn.slice(5, 7))}`,
              ),
            })
          : schedule.recurrence === 'once'
            ? t('scheduling.summaryOnce', {
                date: schedule.startsOn.split('-').reverse().join('/'),
              })
            : t(`scheduling.recurrence_${schedule.recurrence}`);
  return schedule.deliveryMode === 'shift_start'
    ? `${wanneer} · ${t('scheduling.summaryShift', { group: schedule.shiftGroupName ?? '' })}`
    : `${wanneer} · ${schedule.timeOfDay}`;
}

/**
 * "Inplannen" in de berichteditor (Stap 14).
 *
 * Het concept is het sjabloon: op elk moment maakt de server er een kopie van en publiceert die.
 * Rechts staan de eerstvolgende vijf data, zodat "maandelijks op de 31ste" geen verrassing wordt.
 */
export function ScheduleDialog({
  open,
  onOpenChange,
  beforeSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Bewaart het concept en geeft zijn id; inplannen gaat over wat er nu in de editor staat. */
  beforeSave: () => Promise<string>;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [recurrence, setRecurrence] = useState<Recurrence>('once');
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1]);
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [monthOfYear, setMonthOfYear] = useState('1');
  const [timeOfDay, setTimeOfDay] = useState('07:00');
  const [startsOn, setStartsOn] = useState(today(1));
  const [endsOn, setEndsOn] = useState('');
  const [deliveryMode, setDeliveryMode] = useState<'at_time' | 'shift_start'>('at_time');
  const [shiftGroupId, setShiftGroupId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const timing: ScheduleTiming = {
    recurrence,
    daysOfWeek: recurrence === 'weekly' ? daysOfWeek : [],
    dayOfMonth: recurrence === 'monthly' || recurrence === 'yearly' ? Number(dayOfMonth) : null,
    monthOfYear: recurrence === 'yearly' ? Number(monthOfYear) : null,
    timeOfDay,
    startsOn,
    endsOn: recurrence !== 'once' && endsOn ? endsOn : null,
    deliveryMode,
    shiftGroupId: deliveryMode === 'shift_start' && shiftGroupId ? shiftGroupId : null,
  };

  const groups = useQuery({
    queryKey: ['directory', 'groups'],
    enabled: open,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/groups');
      return (data as { items: Group[] } | undefined)?.items ?? [];
    },
  });

  // Het voorbeeld: elke wijziging, na een korte pauze, opnieuw gevraagd aan de server - die
  // rekent in de tijdzone van het bedrijf, en de browser van de schrijver misschien niet.
  const [debounced, setDebounced] = useState(timing);
  const key = JSON.stringify(timing);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(JSON.parse(key) as ScheduleTiming), 300);
    return () => clearTimeout(timer);
  }, [key]);
  const preview = useQuery({
    queryKey: ['schedule-preview', debounced],
    enabled: open && !(debounced.deliveryMode === 'shift_start' && !debounced.shiftGroupId),
    retry: false,
    queryFn: async (): Promise<SchedulePreview> => {
      const { data, error: fout } = await api.POST('/api/v1/schedules/preview', {
        body: debounced as never,
      });
      if (fout || !data) throw fout ?? new Error('Mislukt');
      return data as SchedulePreview;
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      const targetId = await beforeSave();
      const { error: fout } = await api.POST('/api/v1/schedules', {
        body: { ...timing, kind: 'post', targetId } as never,
      });
      if (fout) throw fout;
      return targetId;
    },
    onSuccess: async (targetId) => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['schedules'] });
      await queryClient.invalidateQueries({ queryKey: ['schedules-of', targetId] });
      toast.show({ title: t('scheduling.created'), tone: 'success' });
      onOpenChange(false);
    },
    onError: (fout) =>
      setError(
        (fout as { errors?: { message: string }[]; detail?: string }).errors?.[0]?.message ??
          (fout as { detail?: string }).detail ??
          t('scheduling.failed'),
      ),
  });

  const toggleDay = (day: number) =>
    setDaysOfWeek((current) =>
      current.includes(day) ? current.filter((value) => value !== day) : [...current, day].sort(),
    );

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('scheduling.dialogTitle')}
      description={t('scheduling.dialogHint')}
    >
      <div className="flex flex-col gap-element" data-testid="schedule-dialog">
        <div className="flex flex-wrap items-end gap-element">
          <div className="w-48">
            <Select
              label={t('scheduling.recurrence')}
              value={recurrence}
              onChange={(event) => setRecurrence(event.target.value as Recurrence)}
              data-testid="schedule-recurrence"
            >
              {(['once', 'daily', 'weekdays', 'weekly', 'monthly', 'yearly'] as const).map(
                (value) => (
                  <option key={value} value={value}>
                    {t(`scheduling.recurrence_${value}`)}
                  </option>
                ),
              )}
            </Select>
          </div>
          {/* De demo-aanpassing: iets dat over enkele minuten vertrekt (Stap 14). */}
          <Button
            variant="secondary"
            size="sm"
            data-testid="schedule-in-minutes"
            onClick={() => {
              setRecurrence('once');
              setDeliveryMode('at_time');
              setStartsOn(today(0));
              setTimeOfDay(clockIn(3));
            }}
          >
            {t('scheduling.inMinutes')}
          </Button>
        </div>

        {recurrence === 'weekly' ? (
          <div className="flex flex-wrap gap-2" role="group" aria-label={t('scheduling.days')}>
            {WEEKDAYS.map((day) => (
              <button
                key={day}
                type="button"
                aria-pressed={daysOfWeek.includes(day)}
                onClick={() => toggleDay(day)}
                className={
                  daysOfWeek.includes(day)
                    ? 'rounded-pill bg-brand-primary px-3 py-1.5 text-caption font-semibold text-text-on-dark'
                    : 'rounded-pill border border-border px-3 py-1.5 text-caption font-semibold text-text-secondary'
                }
              >
                {t(`scheduling.dayShort${day}`)}
              </button>
            ))}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-element">
          {recurrence === 'yearly' ? (
            <div className="w-40">
              <Select
                label={t('scheduling.month')}
                value={monthOfYear}
                onChange={(event) => setMonthOfYear(event.target.value)}
              >
                {Array.from({ length: 12 }, (_, index) => (
                  <option key={index + 1} value={String(index + 1)}>
                    {t(`scheduling.month${index + 1}`)}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          {recurrence === 'monthly' || recurrence === 'yearly' ? (
            <div className="w-28">
              <Input
                label={t('scheduling.dayOfMonth')}
                type="number"
                min={1}
                max={31}
                value={dayOfMonth}
                onChange={(event) => setDayOfMonth(event.target.value)}
              />
            </div>
          ) : null}
          <div className="w-44">
            <Input
              label={recurrence === 'once' ? t('scheduling.date') : t('scheduling.startsOn')}
              type="date"
              value={startsOn}
              min={today(0)}
              onChange={(event) => setStartsOn(event.target.value)}
              data-testid="schedule-starts-on"
            />
          </div>
          {recurrence !== 'once' ? (
            <div className="w-44">
              <Input
                label={t('scheduling.endsOn')}
                type="date"
                value={endsOn}
                min={startsOn}
                onChange={(event) => setEndsOn(event.target.value)}
              />
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-end gap-element">
          <div className="w-56">
            <Select
              label={t('scheduling.moment')}
              value={deliveryMode}
              onChange={(event) => setDeliveryMode(event.target.value as 'at_time' | 'shift_start')}
              data-testid="schedule-mode"
            >
              <option value="at_time">{t('scheduling.atTime')}</option>
              <option value="shift_start">{t('scheduling.atShiftStart')}</option>
            </Select>
          </div>
          <div className="w-32">
            <Input
              label={
                deliveryMode === 'shift_start' ? t('scheduling.notBefore') : t('scheduling.time')
              }
              type="time"
              value={timeOfDay}
              onChange={(event) => setTimeOfDay(event.target.value)}
              data-testid="schedule-time"
            />
          </div>
          {deliveryMode === 'shift_start' ? (
            <div className="w-56">
              <Select
                label={t('scheduling.shiftGroup')}
                value={shiftGroupId}
                onChange={(event) => setShiftGroupId(event.target.value)}
                data-testid="schedule-shift-group"
              >
                <option value="">—</option>
                {(groups.data ?? []).map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
        </div>

        <div className="rounded-card bg-background p-4" data-testid="schedule-preview">
          <p className="text-caption font-semibold uppercase tracking-wide text-text-muted">
            {t('scheduling.previewTitle')}
          </p>
          {preview.isError ? (
            <p className="mt-1 text-content text-text-secondary">{t('scheduling.previewNone')}</p>
          ) : (preview.data?.dates.length ?? 0) === 0 ? (
            <p className="mt-1 text-content text-text-secondary">
              {preview.isFetching ? t('common.loading') : t('scheduling.previewNone')}
            </p>
          ) : (
            <ol className="mt-1 flex flex-col gap-0.5 text-content text-text-primary">
              {preview.data?.dates.map((date) => (
                <li key={date}>{formatDateTime(date)}</li>
              ))}
            </ol>
          )}
        </div>

        {error ? <Callout tone="danger">{error}</Callout> : null}

        <div className="flex justify-end gap-inline">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            loading={create.isPending}
            disabled={(preview.data?.dates.length ?? 0) === 0}
            onClick={() => create.mutate()}
            data-testid="schedule-save"
          >
            {t('scheduling.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
